import { ENDPOINTS } from "@/api/endpoints";
import { apiClient } from "@/lib/client";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { HTTPError } from "ky";

export type RoleName = "ADMIN" | "USER" | "OWNER" | "SUPER_ADMIN";

type Role = {
  roles: RoleName[];
  isAdmin: boolean;
  isSuperAdmin: boolean;
};

export const useGetRolesQuery = (
  options?: Omit<
    UseQueryOptions<Role, HTTPError, Role, ["roles"]>,
    "queryKey" | "queryFn"
  >
) => {
  const query = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const res = await apiClient.get(ENDPOINTS.ROLES).json<Role>();
      return res;
    },
    select: ({ roles }) => {
      const isSuperAdmin = roles.some((role) => role.includes("SUPER_ADMIN"));
      const isAdmin = roles.some((role) => role.includes("ADMIN"));
      return { roles, isSuperAdmin, isAdmin };
    },
    // A 401/403 here means the session is dead, not that the request was
    // unlucky. Retrying just re-enters the ky beforeRequest mint and fires
    // another doomed /oauth/token round-trip, which is what produced the
    // repeating token -> me -> me loop behind the blank-page bug.
    retry: (failureCount, error) => {
      const status = (error as HTTPError)?.response?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 3;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    ...options,
  });

  return query;
};
